import { AlertTriangle, RefreshCw, Smile } from "lucide-react";
import { motion } from "motion/react";
import { useQueryClient } from "@tanstack/react-query";
import { NotificationItem } from "@/features/notifications/components/NotificationItem";
import { useNotificationsContext } from "@/features/notifications/context/NotificationsContext";
import { useVisibleNotifications } from "@/features/notifications/hooks/useVisibleNotifications";
import { notificationsInvalidationService } from "@/features/notifications/services/notifications-invalidation.service";

export const NotificationDropdown = () => {
  const { notifications } = useNotificationsContext();
  const queryClient = useQueryClient();
  const {
    visibleNotifications, isLoading, isError, hasNotifications,
  } =
    useVisibleNotifications();

  const handleRefresh = () => {
    notificationsInvalidationService.invalidateNotifications(queryClient);
  };

  if (isError) {
    return (
      <div className="w-64 text-sm text-gray-500 dark:text-gray-400 z-50">
        <div className="flex items-center justify-center gap-3 py-2 bg-error/80 text-white dark:bg-error-900">
          <AlertTriangle className="text-white ml-2" size={36} />
          <span className="text-xs font-medium">
            Une erreur est survenue lors du chargement des notifications
          </span>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="w-64 text-sm text-gray-500 dark:text-gray-400">
        <div className="py-3 px-4">
          <span className="text-xs">Chargement...</span>
        </div>
      </div>
    );
  }

  if (!hasNotifications) {
    return (
      <div className="w-64 text-sm text-gray-500 dark:text-gray-400">
        <div className="py-3 px-4">
          <span className="flex items-center gap-2">
            Aucune notification
            <Smile size={16} />
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-64 text-sm text-gray-500 dark:text-gray-400">
      <div className="py-2 px-4 border-b border-gray-100 dark:border-gray-600">
        <div className="flex items-center justify-between">
          <span className="block text-sm font-semibold text-gray-900 dark:text-white">
            Notifications
          </span>
          <motion.button
            type="button"
            onClick={handleRefresh}
            whileHover={{ rotate: 360 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label="Actualiser les notifications"
          >
            <RefreshCw size={16} className="text-gray-600 dark:text-gray-400" />
          </motion.button>
        </div>
      </div>
      <ul className="text-gray-700 dark:text-gray-300">
        {visibleNotifications.map((config) => {
          const count = notifications?.[config.key]?.count ?? 0;
          return (
            <NotificationItem
              key={config.key}
              config={config}
              count={count}
            />
          );
        })}
      </ul>
    </div>
  );
};
